import * as React from "react";
import Box from "@mui/joy/Box";
import Loading from "./Utils/Loading";
import { db } from "../FirebaseConfig";
import { Typography } from "@mui/joy";
import { useParams } from "react-router-dom";
import Divider from "@mui/joy/Divider";
import Stack from "@mui/joy/Stack";
import Chip from "@mui/joy/Chip";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";

interface RouteParams {
  [key: string]: string | undefined;
  id: string;
}

interface FirestoreTimestamp {
  seconds: number;
  nanoseconds: number;
}

export default function ReportDetails() {
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState<any>({});
  const { id } = useParams<RouteParams>();

  const FirestoreTimestampToDate = (timestamp?: FirestoreTimestamp): string => {
    if (
      !timestamp ||
      typeof timestamp.seconds !== "number" ||
      typeof timestamp.nanoseconds !== "number"
    ) {
      return "Invalid date"; // or handle the error as needed
    }

    const milliseconds =
      timestamp.seconds * 1000 + timestamp.nanoseconds / 1000000;
    const date = new Date(milliseconds);
    const formattedDate = date.toLocaleDateString();
    return formattedDate;
  };

  React.useEffect(() => {
    const getData = async () => {
      setLoading(true);
      try {
        const eventsSnapshot = await db.collection("Events").get();

        for (const eventDoc of eventsSnapshot.docs) {
          const eventName = eventDoc.id;
          const reportDoc = await eventDoc.ref
            .collection("Reports")
            .doc(id)
            .get();
          if (reportDoc.exists) {
            const reportData = reportDoc.data();
            setData({ id: reportDoc.id, eventName, ...reportData });
            setLoading(false);
            return;
          }
        }

        setLoading(false);
        setData(null);
      } catch (error) {
        console.error("Error fetching report details:", error);
        setLoading(false);
      }
    };

    getData();
  }, [id]);

  const stepsList = data.stepstoreproduce
    ? data.stepstoreproduce.split(",").filter(Boolean)
    : [];

  return loading ? (
    <Loading />
  ) : (
    <Box sx={{ flex: 1, width: "100%" }}>
      <Divider>
        <Chip variant="soft" color="neutral" size="md">
          Submission Details
        </Chip>
      </Divider>
      <div className="pt-4"></div>
      <Stack
        spacing={1}
        direction="row"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography level="title-lg">
            Report ID <br />
          </Typography>
          <Typography level="body-lg">{data.id}</Typography>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography level="title-lg">
            Submitted On <br />
          </Typography>
          <Typography level="body-lg">
            {FirestoreTimestampToDate(data.submittedAt)}
          </Typography>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography level="title-lg">
            Submitted By <br />
          </Typography>
          <Typography level="body-lg">{data.submittedByName}</Typography>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography level="title-lg">
            Status <br />
          </Typography>
          <Typography level="body-lg">{data.status}</Typography>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography level="title-lg">
            Score <br />
          </Typography>
          <Typography level="body-lg">{data.score}</Typography>
        </div>
      </Stack>
      <div className="pt-4"></div>
      <Divider>
        <Chip variant="soft" color="neutral" size="md">
          Report Details
        </Chip>
      </Divider>
      <div className="pt-4"></div>
      <Typography level="h3" sx={{ padding: "20px" }}>
        Report Title:{" "}
        <Box component="span" sx={{ fontWeight: "normal" }}>
          <Typography level="body-lg" textAlign={"justify"}>
            {data.reporttitle}
          </Typography>
        </Box>
      </Typography>
      <Stack
        spacing={1}
        direction="row"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography level="h3" textAlign={"center"}>
            Affected Component/URL
          </Typography>
          <div className="pt-2"></div>
          <Typography level="body-lg" textAlign={"center"}>
            {data.affectedcomponent}
          </Typography>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography level="h3" textAlign={"center"}>
            Vulnerability Type
          </Typography>
          <div className="pt-2"></div>
          <Typography level="body-lg">{data.vulnerabilitytype}</Typography>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography level="h3">Severity</Typography>
          <div className="pt-2"></div>
          <Typography level="body-lg">{data.severitylevel}</Typography>
        </div>
      </Stack>
      <div className="pt-4"></div>
      <Stack
        spacing={1}
        direction="row"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "justify",
          }}
        >
          <Typography level="h3">Report Description</Typography>
          <div className="pt-2"></div>
          <Typography level="body-lg">{data.reportdescription}</Typography>
        </div>
      </Stack>
      <div className="pt-4"></div>
      <Typography level="h3" textAlign={"center"}>
        Steps to Reproduce
      </Typography>
      <div className="pt-2"></div>
      <List marker={"decimal"}>
        {stepsList.map((step: string, index: number) => (
          <ListItem key={index}>
            <Typography level="body-lg" textAlign={"justify"}>
              {step.trim()}
            </Typography>
          </ListItem>
        ))}
      </List>
      <div className="pt-4"></div>
      <Divider>
        <Chip variant="soft" color="neutral" size="md">
          Attachments
        </Chip>
      </Divider>
      <div className="pt-4"></div>
      {data.fileUrl && (
        <iframe
          src={data.fileUrl}
          style={{ width: "100%", height: "1000px" }}
          frameBorder="0"
          allowFullScreen
        >
          Your browser does not support PDFs.
        </iframe>
      )}
    </Box>
  );
}
